import { rrulestr } from './rrule'
import { z } from 'zod'
import { CERTITUDES, DEVISE_PAR_DEFAUT, MOYENS_PAIEMENT, SENS_PAIEMENT } from './domaine'
import { modeleTache } from './taches'
import { verificationRegle } from './verifications'

// Schémas d'entrée des règles et des échéances, partagés par l'API et le MCP.

const texte = z.string().trim().min(1)
const jourCivil = z.iso.date()
const centimes = z.number().int().nonnegative()

const rrule = z
  .string()
  .trim()
  .refine((valeur) => {
    if (!/^DTSTART[:;]/m.test(valeur)) return false
    try {
      return rrulestr(valeur).options.freq !== undefined
    } catch {
      return false
    }
  }, 'Récurrence invalide : DTSTART puis RRULE, par exemple « DTSTART:20260101T000000Z » et « RRULE:FREQ=MONTHLY;BYMONTHDAY=15 »')

export const parametresRegle = z.object({
  periode: z.object({
    unite: z.enum(['mois', 'trimestre', 'annee']),
    decalage: z.number().int().min(-24).max(24),
  }),
  fenetreJours: z.number().int().min(0).max(366).optional(),
  reportJourOuvre: z.boolean().optional(),
})

/**
 * Paiement attendu de chaque échéance qui porte un montant : sens, moyen et, si on le
 * connaît, le tiers. Sans lui, la règle ne crée que des échéances et des tâches.
 */
export const paiementRegle = z.object({
  sens: z.enum(SENS_PAIEMENT),
  moyen: z.enum(MOYENS_PAIEMENT),
  tiersId: z.uuid().nullable().optional(),
})

const champsRegle = {
  libelle: texte,
  rrule,
  decalage: parametresRegle,
  certitude: z.enum(CERTITUDES),
  montantPrevuCts: centimes.nullable(),
  devise: z.string().regex(/^[A-Z]{3}$/),
  note: texte.nullable(),
  active: z.boolean(),
  taches: z.array(modeleTache).max(20),
  /** Bail de la règle (loyer, attestation annuelle…) : son gestionnaire peut exécuter. */
  bailId: z.uuid().nullable(),
  paiement: paiementRegle.nullable(),
  /** Vérification : constate seule, à chaque période, ce qui doit arriver (relevé ou pièce). */
  verification: verificationRegle.nullable(),
}

export const regleCreation = z.object({
  type: z.string().regex(/^[a-z0-9_]+$/, 'Code en minuscules : tva_ca3, cfe…'),
  entiteId: z.uuid(),
  ...champsRegle,
  certitude: champsRegle.certitude.default('a_confirmer'),
  montantPrevuCts: champsRegle.montantPrevuCts.optional(),
  devise: champsRegle.devise.default(DEVISE_PAR_DEFAUT),
  note: champsRegle.note.optional(),
  active: champsRegle.active.default(true),
  taches: champsRegle.taches.default([]),
  bailId: champsRegle.bailId.optional(),
  paiement: champsRegle.paiement.optional(),
  verification: champsRegle.verification.optional(),
})

export const regleModification = z.object(champsRegle).partial()

/** Marquer fait : preuve (pièce) et montant réel facultatifs ; la simple coche suffit. */
export const marquerFait = z.object({
  preuvePieceId: z.uuid().nullable().optional(),
  montantReelCts: centimes.nullable().optional(),
})

/**
 * Modification à la main : une nouvelle date devient une date forcée, que les
 * générateurs ne touchent plus. « dateForcee: false » rend l'échéance au générateur.
 */
export const echeanceModification = z
  .object({
    libelle: texte,
    date: jourCivil,
    finFenetre: jourCivil.nullable(),
    montantPrevuCts: centimes.nullable(),
    dateForcee: z.literal(false),
  })
  .partial()
  .refine((e) => !e.date || !e.finFenetre || e.finFenetre >= e.date, {
    message: 'La fin de fenêtre ne peut précéder la date.',
    path: ['finFenetre'],
  })

export type RegleCreation = z.infer<typeof regleCreation>
export type RegleModification = z.infer<typeof regleModification>
export type MarquerFait = z.infer<typeof marquerFait>
export type EcheanceModification = z.infer<typeof echeanceModification>
